import { useEffect } from 'react';
import md5 from 'crypto-js/md5';
import { injectWidget } from 'watermelon-widget-injection';
import { User } from '../../services/app';
import { OFFERS_PROD, OFFERS_STAGING, OFFERS_TEST } from '../../config/offers';
import { isProduction } from '../../utils/Location';

type WatermelonTags = {
  company_id: number;
  created_at: string;
  deleted_at: string | null;
  id: number;
  name: string;
  updated_at: string | null;
}

type ConversationMetaPayload = {
  conversationId: number;
  companyId: number;
  userId: number;
  webchatSocketChannel: string;
  tags: Array<WatermelonTags>;
};

type SocketPayload = {
  type?: string;
  payload?: unknown;
};

type ExpiringStorageValue = {
  value: any;
  expiresAt: number;
};

type SocketTicketResponse = {
  ticket: string;
  expiresIn: number;
};

const
  // Messages from socketServer
  INCOMING_WATERMELON_MESSAGE_EVENT = 'message',
  INCOMING_CONVERSATION_META_RESPONSE = 'conversation_meta_response',
  // Outcoming message types for socket server
  OUTCOMING_CONNECTED_EVENT = 'connected',
  OUTCOMING_GET_CONVERSATION_META = 'get_conversation_meta',
  OUTCOMING_POST_MESSAGE = 'post_message',

  // localStorage keys and other constants
  USER_ID_STORAGE_KEY = 'userId',
  CONVERSATION_META_STORAGE_KEY = 'conversationMeta',
  NO_ACTIVE_DEALS_MESSAGE_SHOWN = 'noActiveDealsMessageShown',
  BACKUP_DEAL_ACTIVATED = 'backupDealActivated',
  BACKUP_DEAL_ACTIVATION_ERROR = 'backupDealActivationError',

  // WatermelomAI tag name
  WATERMELOMN_DEALS_PROBLEM_TAG = 'Deal problem',

  // TTL
  FIFTEEN_MINUTES_IN_MS = 15 * 60 * 1000,
  DAY_IN_MS = 24 * 60 * 60 * 1000,
  WEEK_IN_MS = 7 * DAY_IN_MS;

const messages = Object.freeze({
  noDeals: 'You have no active deals at the moment.',
  fetching: 'Issue with a deals detected. Fetching your deals information...',
  failedActivate: 'Failed to activate backup deal: ',
  tryAgain: 'Please try again in 15 minutes.',
  dealsCurrent: (activeDealTitle: string, activeDealExpiry: string) =>
    `Your current active deal: ${activeDealTitle} (expires at ${activeDealExpiry}).`,
  dealsNew: (newDeal: string, newDealId: number) =>
    `Your new active deal: ${newDeal} (#${newDealId}).`,
});

const messagesDutch = Object.freeze({
  noDeals: 'Je hebt momenteel geen actieve deals.',
  fetching: 'Er is een probleem met je deals gedetecteerd. We halen je deal informatie op...',
  failedActivate: 'Het activeren van de back-updeal is mislukt: ',
  tryAgain: 'Probeer het over 15 minuten opnieuw.',
  dealsCurrent: (activeDealTitle: string, activeDealExpiry: string) =>
    `Je huidige actieve deal: ${activeDealTitle} (verloopt op ${activeDealExpiry}).`,
  dealsNew: (newDeal: string, newDealId: number) =>
    `Je nieuwe actieve deal: ${newDeal} (#${newDealId}).`,
});

function getLocalizedMessages(language: string) {
  return language === 'nl' ? messagesDutch : messages;
}

function setStoredValueWithTTL(key: string, value: any, ttlMs: number) {
  const wrappedValue: ExpiringStorageValue = {
    value,
    expiresAt: Date.now() + ttlMs,
  };

  setStoredValue(key, JSON.stringify(wrappedValue));
}

function getStoredValueWithTTL(key: string) {
  const rawValue = getStoredValue(key);

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<ExpiringStorageValue>;

    if (typeof parsed.value === 'undefined' || typeof parsed.expiresAt !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function getStoredConversationMeta(): Partial<ConversationMetaPayload> {
  const rawConversationMeta = localStorage.getItem(CONVERSATION_META_STORAGE_KEY);

  if (!rawConversationMeta) {
    return {};
  }

  try {
    return JSON.parse(rawConversationMeta) as Partial<ConversationMetaPayload>;
  } catch {
    return {};
  }
}

function getStoredValue(key: string) {
  return localStorage.getItem(key);
}

function setStoredValue(key: string, value: string) {
  localStorage.setItem(key, value);
}

function getOrCreateUserId() {
  const existingUserId = getStoredValue(USER_ID_STORAGE_KEY);
  if (existingUserId) {
    console.log('Found userId in localStorage: ', existingUserId);
    return existingUserId;
  }
  const newUserId = createMd5UserId();
  console.log('Creating new userId in localStorage: ', newUserId);
  setStoredValue(USER_ID_STORAGE_KEY, newUserId);
  return newUserId;
}

function createMd5UserId() {
  return md5(`${crypto.randomUUID()}-${Date.now()}`).toString();
}

function resolveUrl(rawBaseUrl: string, queryParams: Record<string, string> = {}) {
  const runtimeHost = window.location.hostname;
  const isRuntimeLocal = runtimeHost === 'localhost' || runtimeHost === '127.0.0.1';

  const parsed = new URL(rawBaseUrl);

  // If page is served remotely, localhost in the URL points to the wrong machine.
  if (!isRuntimeLocal && parsed.hostname === 'localhost') {
    parsed.hostname = runtimeHost;
  }

  // Avoid mixed-content blocking when app runs under HTTPS.
  if (window.location.protocol === 'https:' && parsed.protocol === 'ws:') {
    parsed.protocol = 'wss:';
  }

  Object.entries(queryParams).forEach(([key, value]) => {
    parsed.searchParams.set(key, value);
  });

  return parsed.toString();
}

function resolveSocketTicketUrl(url: string) {
  const parsed = new URL(url);
  if (parsed.protocol === 'ws:') parsed.protocol = 'http:';
  if (parsed.protocol === 'wss:') parsed.protocol = 'https:';
  parsed.pathname = '/socket-auth/ticket';
  parsed.search = '';
  parsed.hash = '';
  return resolveUrl(parsed.toString());
}

async function fetchSocketTicket(ticketUrl: string, userId: string) {
  const response = await fetch(ticketUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch socket ticket. Status: ${response.status}`);
  }

  const parsed = (await response.json()) as Partial<SocketTicketResponse>;

  if (!parsed.ticket || typeof parsed.ticket !== 'string') {
    throw new Error('Socket ticket response is missing a valid "ticket" field.');
  }

  return parsed.ticket;
}

function getRandomElement<T>(items: T[]): T {
  if (items.length === 0) throw new Error('Cannot get random element from an empty array');
  const randomIndex = Math.floor(Math.random() * items.length);
  return items[randomIndex];
}

export function WatermelonLoginChat({ language }: { language: string }) {
  const syncConversationMeta = (nextConversationMeta: Partial<ConversationMetaPayload>) => {
    localStorage.setItem(CONVERSATION_META_STORAGE_KEY, JSON.stringify(nextConversationMeta));
  };

  function buildMessagePayload(message: string) {
    const latestConversationMeta = getStoredConversationMeta();
    const conversationId = latestConversationMeta?.conversationId;
    if (!conversationId) throw new Error('Conversation ID is not set. Cannot build message payload.');

    return {
      conversation_id: conversationId,
      contact_id: null,
      company_id: latestConversationMeta?.companyId,
      user_id: latestConversationMeta?.userId,
      type: 'text',
      channel: 'watermelon',
      socket: latestConversationMeta?.webchatSocketChannel,
      payload: message,
    };
  }

  useEffect(() => {
    const widgetId = 'kW605qU1zEJ';
    const settingsId = '16495';
    const userId = getOrCreateUserId();
    const env = 'prod';
    const first = 'Chatbot';
    const last = `User ${userId}`;
    const email = `not_real_${userId}@test.com`;
    const phone = '+' + Math.random().toString().slice(2, 11);
    const socketBaseUrl = import.meta.env.VITE_CHATBOT_SOCKET_URL as string;
    const socketAuthUrl = import.meta.env.VITE_CHATBOT_SOCKET_AUTH_URL as string;
    const localizedMessages = getLocalizedMessages(language);

    let socket: WebSocket | null = null;
    let loginTimeout: number | null = null;
    let metaInterval: number | null = null;
    let isSocketClosed = false;

    const isSocketOpen = () => !!socket && socket.readyState === WebSocket.OPEN && !isSocketClosed;

    const sendSocketMessage = (payload: unknown) => {
      const currentSocket = socket;
      if (!currentSocket || !isSocketOpen()) {
        throw new Error(`[Socket client] Message sending skipped: ${JSON.stringify(payload)}`);
      }

      console.log('[Socket client] Outcoming message:', payload);
      currentSocket.send(JSON.stringify(payload));
    };

    const conversationPollingHandler = async () => {
      const skip = getStoredValueWithTTL(NO_ACTIVE_DEALS_MESSAGE_SHOWN);
      const backupDealActivated = getStoredValueWithTTL(BACKUP_DEAL_ACTIVATED);
      const backupDealActivationError = getStoredValueWithTTL(BACKUP_DEAL_ACTIVATION_ERROR);
      const latestConversationMeta = getStoredConversationMeta();
      const hasIssues = latestConversationMeta.tags?.some(tag => tag.name === WATERMELOMN_DEALS_PROBLEM_TAG);

      // Ignore sending anything to the chat
      if (!hasIssues || backupDealActivated || backupDealActivationError) {
        return false;
      }

      sendSocketMessage({
        type: OUTCOMING_POST_MESSAGE,
        payload: buildMessagePayload(localizedMessages.fetching),
      });

      // const offers = await User.checkRedeemedOffers();
      // const now = new Date();
      // const activeDeal = offers.redeemedOffers?.find(
      //   o => now >= new Date(o.startDate) && now <= new Date(o.endDate),
      // );

      // if (!activeDeal) {
      //   setStoredValueWithTTL(NO_ACTIVE_DEALS_MESSAGE_SHOWN, true, FIFTEEN_MINUTES_IN_MS * 4 * 7);
      //   sendSocketMessage({
      //     type: OUTCOMING_POST_MESSAGE,
      //     payload: buildMessagePayload(localizedMessages.noDeals),
      //   });
      //   return false;
      // }

      // Clean test: always hidden stampcard 5960 + reward 320941
        const randomDeal = { id: 320941, name: 'PCP: 17 Bucket (Spicy) McChicken of (Spicy) Meatless Chicken Burger of BM Groot Voordeelmenus voor €10,15' };
        console.log(`[Debug] Activating 5960, deal ${randomDeal.id}`);

        try {
        const activationResult = await User.activateBackupDeal(5960, randomDeal.id);

        sendSocketMessage({
          type: OUTCOMING_POST_MESSAGE,
          payload: buildMessagePayload(
            // localizedMessages.dealsCurrent(activeDeal?.title, activeDeal?.endDate) +
            // "\n" +
            localizedMessages.dealsNew(randomDeal.name, randomDeal.id)
          ),
        });

        setStoredValueWithTTL(BACKUP_DEAL_ACTIVATED, true, DAY_IN_MS);

        return activationResult;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        console.error('Failed to activate backup deal:', error);

        setStoredValueWithTTL(BACKUP_DEAL_ACTIVATION_ERROR, true, FIFTEEN_MINUTES_IN_MS);

        sendSocketMessage({
          type: OUTCOMING_POST_MESSAGE,
          payload: buildMessagePayload(`${localizedMessages.failedActivate} (${errorMessage}).` +
            `\n${localizedMessages.tryAgain}`),
        });

        return false;
      }
    };

    if (socketBaseUrl) {
      const connectSocket = async () => {
        try {
          const ticketUrl = resolveSocketTicketUrl(socketAuthUrl);
          const ticket = await fetchSocketTicket(ticketUrl, userId);
          const socketUrl = resolveUrl(socketBaseUrl, { ticket });

          socket = new WebSocket(socketUrl);

          // Connected event
          socket.onopen = () => {
            isSocketClosed = false;
            sendSocketMessage({
              type:  OUTCOMING_CONNECTED_EVENT,
              payload: {
                text: 'User connected from frontend',
                userId,
                email,
                phone,
              },
            });
          };

          // Incoming messages from the socketServer
          socket.onmessage = async event => {
            console.log(`[Socket client] Incoming message`, { type: event.type, data: JSON.parse(event.data) });

            let parsed: SocketPayload | null = null;
            try {
              parsed = typeof event.data === 'string' ? (JSON.parse(event.data) as SocketPayload) : null;
            } catch {
              parsed = null;
            }

            // Conversation meta handler
            if (parsed?.type === INCOMING_CONVERSATION_META_RESPONSE) {
              const nextConversationMeta = parsed.payload as ConversationMetaPayload;

              syncConversationMeta(nextConversationMeta);

              await conversationPollingHandler();
            }
          };

          socket.onerror = error => {
            console.error('Socket error:', error);
          };

          socket.onclose = event => {
            isSocketClosed = true;
            console.warn(
              `Socket closed (code=${event.code}, wasClean=${event.wasClean}, reason=${event.reason || 'none'})`,
            );
          };
        } catch (error) {
          console.error('Failed to initialize authenticated socket:', error);
        }
      };

      void connectSocket();
    }

    const onLogin = () => {
      loginTimeout = window.setTimeout(() => {
        const iframe = document.querySelector('.watermelon-embed-frame') as HTMLIFrameElement;
        iframe.contentWindow?.postMessage(
          {
            type: 'login',
            payload: {
              first_name: first,
              last_name: last,
              email,
              phone,
              user_id: userId,
              custom_fields: {
                cookieId: userId,
              },
            },
          },
          '*',
        );
      }, 2000); // timeout is needed because of endpoint and iframe embed latency
    };

    const initialize = () => {
      injectWidget(widgetId, settingsId, env, first, last, email, phone, userId);
      onLogin();
    };

    initialize();

    // Push the widget below the close button on every device (notch or not)
    function applyNotchOffset() {
      const selectors = ['.watermelon-embed-frame', '.watermelon-embed-wrapper', '.watermelon-embed'];
      const found = selectors.some(sel => !!document.querySelector(sel));
      if (!found) return;

      // Use the actual rendered bottom of the close button as the widget top.
      // Falls back to safe-top + 70 px if the button isn't mounted yet.
      const closeBtn = document.querySelector('[data-close-btn]') as HTMLElement | null;
      const widgetTop = closeBtn
        ? Math.ceil(closeBtn.getBoundingClientRect().bottom) + 8
        : (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-top')) || 0) + 70;

      selectors.forEach(sel => {
        const el = document.querySelector(sel) as HTMLElement | null;
        if (!el) return;
        el.style.position = 'fixed';
        el.style.top = `${widgetTop}px`;
        el.style.left = '0';
        el.style.right = '0';
        el.style.bottom = '0';
        el.style.height = `${window.innerHeight - widgetTop}px`;
        el.style.width = '100%';
      });
    }

    const notchObserver = new MutationObserver(applyNotchOffset);
    notchObserver.observe(document.body, { childList: true, subtree: true });
    // Fallback timer in case the element is already there
    const notchTimer = window.setTimeout(applyNotchOffset, 800);

    // Incoming message from Watermelon after post message from the client
    const onWindowMessage = (event: MessageEvent) => {

      // Response on each message. Before this, we don't have conversationId
      if (event.data.command === INCOMING_WATERMELON_MESSAGE_EVENT) {

        console.log('[Window] Received message:', event.data);

        const payload = event.data.payload;
        const conversationId = payload?.conversation_id;
        if (!conversationId || typeof conversationId !== 'number') {
          return;
        }

        sendSocketMessage({
          type: OUTCOMING_GET_CONVERSATION_META,
          payload: {
            conversationId,
          },
        });
      }
    };

    window.addEventListener(INCOMING_WATERMELON_MESSAGE_EVENT, onWindowMessage);

    // Long polling
    metaInterval = window.setInterval(() => {
      const conversationId = getStoredConversationMeta()?.conversationId;
      if (!conversationId) return;
      sendSocketMessage({
        type: OUTCOMING_GET_CONVERSATION_META,
        payload: { conversationId },
      });
    }, 7000);

    return () => {
      if (loginTimeout !== null) {
        window.clearTimeout(loginTimeout);
      }
      if (metaInterval !== null) {
        window.clearInterval(metaInterval);
      }
      window.clearTimeout(notchTimer);
      notchObserver.disconnect();
      window.removeEventListener(INCOMING_WATERMELON_MESSAGE_EVENT, onWindowMessage);
      socket?.close();
    };
  }, []);

  return null;
}
